import { USD } from "@dinero.js/currencies";
import { dinero } from "dinero.js";
import _, { compact } from "lodash";
import { z } from "zod";
import { BaseConnector } from "../baseConnector";
export class LCSCConnector extends BaseConnector {
    getScraperApiKey;
    constructor({ getScraperApiKey }) {
        super();
        this.getScraperApiKey = getScraperApiKey;
    }
    get baseUrl() {
        // NOTE: sometime in spring 2024, the LCSC API endpoint changed from
        // "https://wmsc.lcsc.com/wmsc", and from POST to GET
        return "https://wmsc.lcsc.com/ftps/wm";
    }
    async search(params) {
        let keyword;
        if ("keyword" in params) {
            keyword = params.keyword;
        }
        else if ("mpn" in params) {
            keyword = params.mpn;
        }
        else if ("dpn" in params && params.distributor === "lcsc") {
            keyword = params.dpn;
        }
        // lcsc.com does not support filtering in its global search, so we just
        // append the manufacturer to the keyword. Empirically, search results that
        // match on the added term rank higher, so we get some benefit.
        if ("manufacturer" in params && params.manufacturer) {
            keyword += " " + params.manufacturer;
        }
        if (!keyword) {
            return [];
        }
        // lcsc cdn rate limits us so we use proxies
        const ScraperAPILKey = this.getScraperApiKey();
        let proxyConfig = {};
        // only run in production
        if (process.env.NODE_ENV === "production") {
            proxyConfig = {
                proxy: {
                    host: `proxy-server.scraperapi.com`,
                    port: 8001,
                    auth: { username: "scraperapi", password: ScraperAPILKey },
                    protocol: "http",
                },
            };
        }
        const initialResponse = await this.get(`/search/global?keyword=${keyword}`, proxyConfig);
        let parsedResponse = LCSCSearchResponseSchema.parse(initialResponse.payload);
        if (parsedResponse.code === 200) {
            // Sometime searches have zero results but the response contains a Product Tip with what to search for
            if (!parsedResponse.result.productSearchResultVO &&
                parsedResponse.result.tipProductDetailUrlVO?.productModel) {
                const response = await this.get(`/search/global?keyword=${parsedResponse.result.tipProductDetailUrlVO.productModel.trim()}`, proxyConfig);
                parsedResponse = LCSCSearchResponseSchema.parse(response.payload);
            }
        }
        return processSearchPayload(parsedResponse);
    }
}
/**
 * LCSC does not have an official API, so we hacked one here by querying their
 * internal-but-public endpoints.
 *
 * This approach has been used by several open source projects, notably:
 * https://github.com/tachiniererin/nargh
 * and https://github.com/LCSC-Dumper/LCSC-Dumper
 */
function processSearchPayload(payload) {
    if (payload.code !== 200) {
        throw new Error(`LCSC search failed: code ${payload.code}: ${JSON.stringify(payload.msg)}`);
    }
    if (!payload.result.productSearchResultVO) {
        return [];
    }
    return payload.result.productSearchResultVO.productList.map((part) => {
        return {
            distributor: "lcsc",
            mpn: part.productModel,
            dpn: part.productCode,
            availableQuantity: part.stockNumber,
            minimumOrderQuantity: part.minBuyNumber,
            datasheetUrl: part.pdfUrl,
            description: stripHTML(part.productIntroEn),
            leadTimeDays: null,
            lifecycleStatus: null,
            manufacturerName: stripHTML(part.brandNameEn),
            packagingOptions: compact([part.productArrange]),
            priceBreaks: part.productPriceList.map((pb) => {
                return {
                    quantity: pb.ladder,
                    price: dinero({ amount: Math.round(pb.usdPrice * 100000), currency: USD, scale: 5 }),
                    currency: pb.currencySymbol,
                };
            }),
            listingUrl: part.url,
            distributorAttributes: {
                distributor: "lcsc",
                weight: part.weight,
                productUnit: part.productUnit,
                maxBuyNumber: part.maxBuyNumber,
                minPacketUnit: part.minPacketUnit,
                minPacketNumber: part.minPacketNumber,
                isReel: part.isReel,
                reelPrice: part.reelPrice,
                encapStandard: part.encapStandard,
                brandId: part.brandId,
                catalogId: part.catalogId,
                catalogName: part.catalogName,
                catalogCode: part.catalogCode,
                parentCatalogId: part.parentCatalogId,
                parentCatalogName: part.parentCatalogName,
                parentCatalogCode: part.parentCatalogCode,
                productDescEn: part.productDescEn,
                isHasBattery: part.isHasBattery,
                isPreSale: part.isPreSale,
                isOnsale: part.isOnsale,
                isForeignOnsale: part.isForeignOnsale,
                isDiscount: part.isDiscount,
                isHot: part.isHot,
                isEnvironment: part.isEnvironment,
                productLadderPrice: part.productLadderPrice,
                ladderDiscountRate: part.ladderDiscountRate,
                stockJs: part.stockJs,
                stockSz: part.stockSz,
                stockHk: part.stockHk,
                wmStockHk: part.wmStockHk,
                domesticStockVO: part.domesticStockVO,
                overseasStockVO: part.overseasStockVO,
                shipImmediately: part.shipImmediately,
                ship3Days: part.ship3Days,
                smtAloneNumberSz: part.smtAloneNumberSz,
                smtAloneNumberJs: part.smtAloneNumberJs,
                productImageUrl: part.productImageUrl,
                productImageUrlBig: part.productImageUrlBig,
                productImages: part.productImages,
                pdfLinkUrl: part.pdfLinkUrl,
                shareCostPrice: part.shareCostPrice,
                activityId: part.activityId,
                activityName: part.activityName,
                activityStartTime: part.activityStartTime,
                activityEndTime: part.activityEndTime,
                stockPrice: part.stockPrice,
                params: _.reduce(part.paramVOList, (acc, param) => {
                    acc[param.paramNameEn] = param.paramValueEn;
                    return acc;
                }, {}),
            },
        };
    });
}
// Some string fields have random <span> tags used to highlight matching search terms on the web, so we strip them out.
function stripHTML(html) {
    return html.replace(/<\/?[^>]+(>|$)/g, "");
}
// We're really only interesed in the product search results right now so the rest of the schema is incomplete.
const LCSCSearchResponseSchema = z.object({
    code: z.number(),
    msg: z.string().nullable(),
    result: z.object({
        catalogVOS: z.lazy(() => z.array(LCSCCatalogResultSchema).nullable().optional()),
        productSearchResultVO: z.lazy(() => LCSCProductSearchResultSchema.nullable().optional()),
        lcOrderLastPage: z.number().optional().nullable(),
        productTotalPage: z.number().nullable().optional(),
        brandHomeVO: z.unknown(),
        brandUrlMap: z.unknown(),
        isToBrand: z.unknown(),
        isToDetail: z.unknown(), // searching for a LCSC part number such as C411675 will go directly to the product page https://lcsc.com/product-detail/C411675.html
        lcBookProductResultVO: z.unknown(),
        tipProductDetailUrlVO: z.lazy(() => LCSCTipProductDetailUrlSchema.nullable().optional()),
    }),
});
const LCSCCatalogResultSchema = z.union([
    z.object({
        catalogId: z.number(),
        parentId: z.number().nullable(),
        parentName: z.string().nullable(),
        catalogName: z.string().nullable(),
        catalogNameEn: z.string().nullable(),
        productNum: z.number().nullable(),
    }),
    // TODO: is this the only type now returned?
    z.object({
        catalogId: z.number(),
        catalogNameEn: z.string().nullable(),
        productNum: z.number().nullable(),
        childCatelogs: z.array(z.object({
            catalogId: z.number(),
            catalogNameEn: z.string().nullable(),
            productNum: z.number().nullable(),
        })),
    }),
]);
const LCSCProductSearchResultSchema = z.object({
    totalCount: z.number(),
    currentPage: z.number(),
    pageSize: z.number(),
    productList: z.lazy(() => z.array(LCSCPartSchema)),
});
const LCSCTipProductDetailUrlSchema = z.object({
    productCode: z.string().optional().nullable(),
    productModel: z.string().optional().nullable(),
    brandNameEn: z.string().optional().nullable(),
    catalogName: z.string().optional().nullable(),
});
const LCSCStockStatusSchema = z.object({
    total: z.number(),
    shipImmediately: z.number(),
    ship3Days: z.number(),
});
const LCSCParamSchema = z.object({
    paramCode: z.string(),
    paramName: z.string(),
    paramNameEn: z.string(),
    paramValueEn: z.string(),
    paramValueEnForSearch: z.number().nullable().optional(),
    isMain: z.boolean().nullable().optional(),
    sortNumber: z.number(),
});
const LCSCPriceBreakSchema = z.object({
    ladder: z.number(),
    productPrice: z.string(),
    discountRate: z.string(),
    currencyPrice: z.number(),
    usdPrice: z.number(),
    currencySymbol: z.string(),
    cnyProductPrice: z.string().optional().nullable(),
});
// Since we're reverse-engineering this API, we might have some surprising
// responses that cause Zod to throw an error when parsing. This is OK - we'll
// be able to respond to errors in Sentry and improve the schema until someday
// it matches reality.
// NOTE: sometime in spring 2024, the LCSC API endpoint changed from
// "https://wmsc.lcsc.com/wmsc", and from POST to GET, and the response format
// changed. We added a bunch of optional() to satisfy zod, sometimes on top of
// nullable().
const LCSCPartSchema = z.object({
    productId: z.number(),
    productCode: z.string(),
    productWeight: z.number().nullable(),
    foreignWeight: z.number().nullable().optional(),
    weight: z.number().nullable(),
    isOnsale: z.boolean().optional().default(false),
    dollarLadderPrice: z.unknown(),
    isForeignOnsale: z.boolean().optional().default(false),
    minBuyNumber: z.number(),
    maxBuyNumber: z.number(),
    isAutoOffsale: z.boolean().optional().default(false),
    minPacketUnit: z.string().optional().nullable(),
    productUnit: z.string().optional().nullable(),
    productArrange: z.string().optional().nullable(),
    minPacketNumber: z.number(),
    encapStandard: z.string(),
    productModel: z.string(),
    brandId: z.number(),
    brandNameEn: z.string(),
    catalogId: z.number(),
    catalogName: z.string(),
    parentCatalogId: z.number().nullable(),
    parentCatalogName: z.string().nullable(),
    productDescEn: z.string().nullable().optional(),
    productIntroEn: z.string(),
    isHasBattery: z.boolean(),
    isDiscount: z.boolean(),
    isHot: z.boolean(),
    isEnvironment: z.boolean(),
    isPreSale: z.boolean(),
    productLadderPrice: z.unknown(),
    ladderDiscountRate: z.unknown(),
    productPriceList: z.array(LCSCPriceBreakSchema),
    stockJs: z.number(),
    stockSz: z.number(),
    stockHk: z.number().optional().nullable(),
    wmStockHk: z.number(),
    domesticStockVO: LCSCStockStatusSchema,
    overseasStockVO: LCSCStockStatusSchema,
    shipImmediately: z.number().optional().nullable(),
    ship3Days: z.number().optional().nullable(),
    smtAloneNumberSz: z.unknown(),
    smtAloneNumberJs: z.unknown(),
    stockNumber: z.number(),
    split: z.number(),
    productImageUrl: z.string().nullable().optional(),
    productImageUrlBig: z.string().nullable().optional(),
    pdfUrl: z.string().nullable(),
    productImages: z.array(z.string()).nullable().optional(),
    paramVOList: z.array(LCSCParamSchema).nullable().optional(),
    isReel: z.boolean(),
    reelPrice: z.number(),
    productModelHighlight: z.string().nullable().optional(),
    productCodeHighlight: z.string().nullable().optional(),
    catalogCode: z.string(),
    parentCatalogCode: z.string().nullable(),
    pdfLinkUrl: z.string().nullable().optional(),
    url: z.string(),
    shareCostPrice: z.unknown(),
    activityId: z.unknown(),
    activityName: z.unknown(),
    activityStartTime: z.unknown(),
    activityEndTime: z.unknown(),
    stockPrice: z.unknown(),
});
//# sourceMappingURL=lcsc.js.map